import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import pdfService from '@/utils/pdfService';
import UserUtils from '@/utils/user';
import { SettingItem } from '@/types/common';
import downloadIcon from '@/assets/svg/download2.svg';
import styles from './GuidesSection.module.scss';
import { adminManagementService } from '@/admin/services/adminManagement';

export const useGuidesItems = (schoolClassCodeEnabled?: boolean): SettingItem[] => {
  const { t } = useTranslation();
  const { user } = useAppSelector(authSelector);

  const userLanguage = user?.metadata.languagePreferences.interfaceLanguage || 'en';
  const appropriatePdfs = pdfService.getAppropriatePdfs(user, userLanguage, schoolClassCodeEnabled);

  return appropriatePdfs.map(pdf => ({
    title: `${t('downloadAndView')} ${t(`${pdf.role}Instructions`)}`,
    showArrow: true,
    customArrowIcon: downloadIcon,
    onTap: async () => {
      const fileName = `${pdf.role}_guide.pdf`;
      if (pdf.role === 'admin') {
        const adminGuideLink = await adminManagementService.getAdminGuideLink();
        try {
          const res = await fetch(adminGuideLink);
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        } catch {
          window.open(adminGuideLink, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      const link = document.createElement('a');
      link.href = pdf.url;
      link.download = fileName;
      link.rel = 'noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  })) as SettingItem[];
};

interface GuidesSectionProps {
  className?: string;
  itemClassName?: string;
  onItemClick?: (pdfUrl: string) => void;
}

const GuidesSection: React.FC<GuidesSectionProps> = ({ className, itemClassName, onItemClick }) => {
  const { t } = useTranslation();
  const { user } = useAppSelector(authSelector);

  if (UserUtils.isStudent(user)) {
    return null;
  }

  const userLanguage = user?.metadata.languagePreferences.interfaceLanguage || 'en';
  const appropriatePdfs = pdfService.getAppropriatePdfs(user, userLanguage);

  const handleClick = async (pdfUrl: string, fileName: string, role: string) => {
    if (onItemClick) {
      onItemClick(pdfUrl);
      return;
    }
    // Admin PDF is fetched as blob to avoid navigation/redirect
    if (role === 'admin') {
      try {
        const res = await fetch(pdfUrl);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = fileName;
    link.rel = 'noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={className}>
      <div className={styles.sectionTitle}>{t('guides')}</div>
      <div className={styles.section}>
        {appropriatePdfs.map((pdf, index) => (
          <button
            key={index}
            className={`${styles.item} ${itemClassName || ''}`}
            onClick={() => handleClick(pdf.url, `${pdf.role}_guide.pdf`, pdf.role)}
          >
            <div className={styles.itemLeft}>
              <span className={styles.itemTitle}>
                {t('admin.download')}{' '}
                <span className={styles.bold}>{t(`${pdf.role}Instructions`)}</span>
              </span>
            </div>
            <img
              src={downloadIcon}
              alt={t('admin.download') || ''}
              width={24}
              height={24}
              className={styles.downloadIcon}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default GuidesSection;
